import { Children, createContext, Fragment, useContext } from "react";
import highlightWords from "highlight-words";

const HighlightContext = createContext<string>("");

export function HighlightProvider({
  value,
  children,
}: {
  value: string;
  children: React.ReactNode;
}) {
  return <HighlightContext.Provider value={value}>{children}</HighlightContext.Provider>;
}

export const useHighlight = () => {
  const context = useContext(HighlightContext);

  return context;
};

export function Highlight<
  T extends keyof JSX.IntrinsicElements | React.JSXElementConstructor<any>,
>({ children, as, ...props }: { as?: T } & Omit<React.ComponentProps<T>, "as">) {
  const Component = as || Fragment;

  const highlight = useHighlight();

  if (!highlight) {
    return <Component {...props}>{children}</Component>;
  }

  const highlightedChildren = Children.map(children, (child) => {
    if (typeof child === "string") {
      const chunks = highlightWords({
        text: child,
        query: highlight,
        matchExactly: true,
      });

      if (chunks.length === 0) {
        return child;
      }

      return chunks.map((chunk) =>
        chunk.match ? (
          <mark
            className="bg-green-200 underline decoration-green-700 decoration-2"
            key={chunk.key}
          >
            {chunk.text}
          </mark>
        ) : (
          <Fragment key={chunk.key}>{chunk.text}</Fragment>
        ),
      );
    }

    return child;
  });

  return <Component {...props}>{highlightedChildren}</Component>;
}
